import React from "react";
import { Link } from "react-router-dom";
import { Star, Heart } from "lucide-react";
import { slugToName } from "../lib/categories";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export default function ProductCard({ p, testIdPrefix = "product" }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: wl = [] } = useQuery({
    queryKey: ["wishlist"],
    queryFn: async () => (await api.get("/wishlist")).data,
    enabled: !!user,
  });
  const inWl = wl.some((w) => w.product_id === p.product_id);

  const toggle = useMutation({
    mutationFn: async () => {
      if (inWl) return (await api.delete(`/wishlist/${p.product_id}`)).data;
      return (await api.post("/wishlist", { product_id: p.product_id })).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["wishlist"] }); toast.success(inWl ? "Removed from wishlist" : "Added to wishlist"); },
  });

  const onHeart = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { toast.error("Sign in to save items"); return; }
    toggle.mutate();
  };

  return (
    <Link
      to={`/product/${p.product_id}`}
      data-testid={`${testIdPrefix}-card-${p.product_id}`}
      className="rh-card group block border border-[#e5e5e5] bg-white relative"
    >
      <button onClick={onHeart} data-testid={`wishlist-toggle-${p.product_id}`}
              className="absolute top-3 right-3 z-10 bg-white border border-[#0a0a0a] p-2 hover:bg-[#0a0a0a] hover:text-white">
        <Heart size={14} className={inWl ? "fill-[#ff3b30] text-[#ff3b30]" : ""} />
      </button>
      <div className="aspect-square bg-[#f5f5f5] overflow-hidden">
        <img src={p.image} alt={p.name} className="w-full h-full object-cover rh-hover-img" loading="lazy" />
      </div>
      <div className="p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="font-mono text-[10px] tracking-widest uppercase text-[#4a4a4a]">{p.brand}</span>
          <span className="font-mono text-[10px] text-[#4a4a4a]">{slugToName[p.category]}</span>
        </div>
        <h3 className="text-sm font-semibold text-[#0a0a0a] line-clamp-2 min-h-[2.5rem] leading-snug">{p.name}</h3>
        <div className="flex items-center gap-1 mt-2">
          <Star size={12} className="fill-[#0a0a0a] text-[#0a0a0a]" />
          <span className="text-xs font-mono">{p.rating?.toFixed(1)}</span>
          <span className="text-xs text-[#4a4a4a] ml-1">({p.reviews})</span>
        </div>
        <div className="mt-4 flex items-baseline justify-between border-t border-[#e5e5e5] pt-3">
          <span className="font-display font-bold text-xl rh-num">${p.price.toFixed(2)}</span>
          <span className="rh-overline text-[10px]">Shop →</span>
        </div>
      </div>
    </Link>
  );
}
